import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Chip, IconButton,
  Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, MenuItem,
} from "@mui/material";
import {
  Refresh, Delete, CheckCircle, Error, Warning,
  Memory, Storage, Speed, Timer, Autorenew, VpnKey, Add, ContentCopy,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const STATUS_COLORS = {
  online: { bg: "rgba(37,211,102,0.1)", color: "#25D366", label: "Online" },
  degraded: { bg: "rgba(245,158,11,0.1)", color: "#F59E0B", label: "Degraded" },
  offline: { bg: "rgba(239,68,68,0.1)", color: "#EF4444", label: "Offline" },
  pending: { bg: "rgba(107,72,255,0.1)", color: "#6B48FF", label: "Pending" },
  suspended: { bg: "rgba(239,68,68,0.1)", color: "#EF4444", label: "Suspended" },
};

/*
 * Estado da credencial do HUB Local (Fase 15, Bloco A).
 *
 * Era INVISIVEL: o token do HUB Local do piloto vencia em 2026-09-28 e nada na tela dizia
 * isso — um HUB saudavel hoje viraria um HUB mudo, sem aviso. Aqui a leitura e humana: o
 * numero de dias sozinho nao diz se e hora de agir.
 */
const TOKEN_NIVEL = {
  ok: { color: "#25D366", bg: "rgba(37,211,102,0.1)", rotulo: "Credencial valida" },
  atencao: { color: "#F59E0B", bg: "rgba(245,158,11,0.12)", rotulo: "Renovar em breve" },
  critico: { color: "#EF4444", bg: "rgba(239,68,68,0.12)", rotulo: "Renovar agora" },
  expirado: { color: "#EF4444", bg: "rgba(239,68,68,0.18)", rotulo: "Credencial expirada" },
  sem_validade: { color: "#6B7280", bg: "rgba(107,114,128,0.12)", rotulo: "Sem validade" },
};

function textoDoPrazo(token) {
  if (!token || token.diasRestantes === null || token.diasRestantes === undefined) return "—";
  const d = token.diasRestantes;
  if (d < 0) return `expirou ha ${Math.abs(d)} dia(s)`;
  if (d === 0) return "expira hoje";
  return `${d} dia(s)`;
}

export default function HubsPage({ showSnackbar }) {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [rotacionando, setRotacionando] = useState(null);

  // Emissao de token de registro (Fase 15, Bloco F): o hrt_ aparece UMA unica vez.
  const [registroOpen, setRegistroOpen] = useState(false);
  const [registroForm, setRegistroForm] = useState({ nomeHub: "", validadeMinutos: 120, tenantId: "" });
  const [registroEmitindo, setRegistroEmitindo] = useState(false);
  const [registroEmitido, setRegistroEmitido] = useState(null); // { token, expiraEm }
  const [tokensPendentes, setTokensPendentes] = useState([]);
  // 22/09/2026: o token nasce para um CLIENTE (tenant). Sem escolher, o Central emitia no
  // proprio tenant e o HUB do cliente nascia no lugar errado. `reconexaoDe` = veio do card.
  const [tenants, setTenants] = useState([]);
  const [reconexaoDe, setReconexaoDe] = useState(null);

  const abrirRegistro = async (hubParaReconectar = null) => {
    setRegistroEmitido(null);
    setReconexaoDe(hubParaReconectar);
    setRegistroForm({
      nomeHub: hubParaReconectar?.name || "",
      validadeMinutos: 120,
      tenantId: hubParaReconectar?.tenantId ? String(hubParaReconectar.tenantId) : "",
    });
    setRegistroOpen(true);
    try {
      const { data } = await hubApi.listarTokensDeRegistro();
      setTokensPendentes(data?.tokens || []);
    } catch { setTokensPendentes([]); }
    try {
      const { data } = await hubApi.adminListTenants();
      setTenants((data?.tenants || data || []).filter((t) => t && t.id));
    } catch { setTenants([]); }
  };

  const emitirToken = async () => {
    if (tenants.length > 0 && !registroForm.tenantId) {
      showSnackbar("Escolha o cliente (tenant) dono deste HUB.", "warning");
      return;
    }
    try {
      setRegistroEmitindo(true);
      const { data } = await hubApi.emitirTokenDeRegistroHub({
        tenantId: registroForm.tenantId ? Number(registroForm.tenantId) : undefined,
        nomeHub: registroForm.nomeHub || undefined,
        validadeMinutos: Number(registroForm.validadeMinutos) || undefined,
      });
      setRegistroEmitido(data);
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.response?.data?.error || "Erro ao emitir token", "error");
    } finally {
      setRegistroEmitindo(false);
    }
  };

  const copiarToken = async () => {
    try {
      await navigator.clipboard.writeText(registroEmitido.token);
      showSnackbar("Token copiado. Cole no painel do HUB Local (Configuracoes → Conexao com HUB Central).");
    } catch {
      showSnackbar("Nao foi possivel copiar — selecione o texto manualmente.", "warning");
    }
  };

  const loadHubs = async () => {
    try {
      setLoading(true);
      const { data } = await hubApi.getHubList();
      setHubs(data.hubs || []);
    } catch (err) {
      console.error("Hubs error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHubs(); }, []);

  const handleDelete = async (hubId) => {
    try {
      await hubApi.deleteHub(hubId);
      showSnackbar("Hub removido");
      loadHubs();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Erro ao remover", "error");
    }
    setDeleteDialog(null);
  };

  /*
   * Renovar NAO derruba o HUB: o Central emite um token novo e mantem o atual valido (e
   * assinando) ate o Local confirmar que persistiu, no proximo heartbeat. Por isso o botao
   * nao pede confirmacao destrutiva — nao ha nada a desfazer.
   */
  const handleRotate = async (hub) => {
    try {
      setRotacionando(hub.id);
      const { data } = await hubApi.rotateHubToken(hub.id);
      showSnackbar(
        data?.message ||
          "Token novo emitido. O HUB Local confirma no proximo heartbeat (ate 30s)."
      );
      loadHubs();
    } catch (err) {
      const resp = err.response?.data;
      if (err.response?.status === 409) {
        showSnackbar(resp?.message || "Ja existe uma renovacao aguardando confirmacao.", "warning");
      } else {
        showSnackbar(resp?.error || "Erro ao renovar o token", "error");
      }
    } finally {
      setRotacionando(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>HUBs Locais</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {hubs.length} hub(s) registrado(s)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => abrirRegistro()}
            sx={{
              background: "linear-gradient(135deg, #6B48FF, #8B5CF6)",
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2,
            }}
          >
            Registrar novo HUB
          </Button>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadHubs} sx={{ color: "#6B7280" }}><Refresh /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {hubs.length === 0 ? (
        <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
          <CardContent sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#6B7280" }}>Nenhum HUB LOCAL registrado</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {hubs.map((hub) => {
            const statusConfig = STATUS_COLORS[hub.status] || STATUS_COLORS.pending;
            const telemetry = hub.lastTelemetry || {};
            const token = hub.token || {};
            const nivelToken = TOKEN_NIVEL[token.nivel] || TOKEN_NIVEL.sem_validade;
            const heartbeatAge = hub.lastHeartbeat
              ? Math.round((Date.now() - new Date(hub.lastHeartbeat).getTime()) / 1000)
              : null;

            return (
              <Grid item xs={12} md={6} key={hub.id}>
                <Card sx={{
                  bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 3, transition: "border-color 0.2s",
                  "&:hover": { borderColor: "rgba(107,72,255,0.3)" },
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: "#fff", fontSize: 15 }}>
                          {hub.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                          ID: {hub.id} — v{hub.version} ({hub.updateChannel})
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          size="small"
                          label={statusConfig.label}
                          sx={{ bgcolor: statusConfig.bg, color: statusConfig.color, fontSize: 11, height: 22 }}
                        />
                        <Tooltip title="Renovar credencial (o token atual segue valido ate o HUB confirmar)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRotate(hub)}
                              disabled={rotacionando === hub.id || token.rotacaoPendente}
                              sx={{ color: token.nivel === "ok" ? "#6B7280" : nivelToken.color }}
                            >
                              <Autorenew fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reconectar: emite um token de registro para colar no HUB Local (use quando o HUB esta offline ou com credencial recusada — o Renovar depende do heartbeat)">
                          <IconButton
                            size="small"
                            onClick={() => abrirRegistro(hub)}
                            sx={{ color: hub.status === "online" ? "#6B7280" : "#F59E0B" }}
                          >
                            <VpnKey fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover hub">
                          <IconButton size="small" onClick={() => setDeleteDialog(hub)} sx={{ color: "#6B7280" }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Metrics */}
                    <Grid container spacing={1.5}>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Timer sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>Heartbeat</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: heartbeatAge && heartbeatAge < 120 ? "#25D366" : "#F59E0B" }}>
                            {heartbeatAge ? `${heartbeatAge}s` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Memory sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>RAM</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#D1D5DB" }}>
                            {telemetry.memoryUsageMB ? `${telemetry.memoryUsageMB}MB` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Speed sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>CPU</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#D1D5DB" }}>
                            {telemetry.cpuLoad != null ? `${(telemetry.cpuLoad * 100).toFixed(0)}%` : "—"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Storage sx={{ fontSize: 16, color: "#6B7280" }} />
                          <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>Queue</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: telemetry.queueSize > 0 ? "#F59E0B" : "#D1D5DB" }}>
                            {telemetry.queueSize ?? "—"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Clone warning */}
                    {hub.cloneSuspicionScore > 30 && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: "rgba(245,158,11,0.06)", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Warning sx={{ fontSize: 14, color: "#F59E0B" }} />
                        <Typography sx={{ fontSize: 11, color: "#F59E0B" }}>
                          Clone suspicion score: {hub.cloneSuspicionScore}
                        </Typography>
                      </Box>
                    )}

                    {/* Credencial (Fase 15, Bloco A) */}
                    <Box
                      sx={{
                        mt: 1.5, p: 1, borderRadius: 1, bgcolor: nivelToken.bg,
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <VpnKey sx={{ fontSize: 14, color: nivelToken.color }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: nivelToken.color }}>
                            {nivelToken.rotulo}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: "#9CA3AF" }}>
                            {`expira em ${textoDoPrazo(token)}`}
                            {token.expiraEm ? ` (${new Date(token.expiraEm).toLocaleDateString()})` : ""}
                            {token.ultimaRotacao
                              ? ` · renovado em ${new Date(token.ultimaRotacao).toLocaleDateString()}`
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                      {token.rotacaoPendente && (
                        <Chip
                          size="small"
                          label="aguardando confirmacao do HUB"
                          sx={{ fontSize: 9, height: 20, bgcolor: "rgba(107,72,255,0.15)", color: "#A78BFA" }}
                        />
                      )}
                    </Box>

                    {/* Footer */}
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 10, color: "#4B5563" }}>
                        Host: {hub.hostname || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#4B5563" }}>
                        Registrado: {new Date(hub.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}>
        <DialogTitle sx={{ color: "#fff" }}>Remover HUB?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#9CA3AF" }}>
            O hub "{deleteDialog?.name}" sera desativado e seus tokens invalidados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: "#6B7280" }}>Cancelar</Button>
          <Button onClick={() => handleDelete(deleteDialog?.id)} variant="contained" color="error">Remover</Button>
        </DialogActions>
      </Dialog>

      {/* Registro de novo HUB Local: emite o token hrt_ de uso unico (Fase 15, Bloco F) */}
      <Dialog
        open={registroOpen}
        onClose={() => !registroEmitindo && setRegistroOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Registrar novo HUB Local</DialogTitle>
        <DialogContent>
          {!registroEmitido ? (
            <>
              <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 2, lineHeight: 1.6 }}>
                {reconexaoDe ? (
                  <>
                    Reconexao do HUB <b>{reconexaoDe.name}</b> (#{reconexaoDe.id}): o token abaixo, colado no
                    HUB Local, <b>renova a credencial</b> sem apagar nada — serve quando o HUB esta offline
                    ou recebendo 401 e o Renovar por heartbeat nao conclui.
                  </>
                ) : (
                  <>
                    Emita um token de registro de <b>uso unico</b> e informe-o ao operador do novo HUB.
                  </>
                )}{" "}
                No painel do HUB Local: Configuracoes → Conexao com HUB Central → Registrar.
              </Typography>
              {tenants.length > 0 && (
                <TextField
                  select
                  label="Cliente (tenant) dono do HUB"
                  fullWidth size="small" sx={{ mb: 2 }}
                  value={registroForm.tenantId}
                  onChange={(e) => setRegistroForm((p) => ({ ...p, tenantId: e.target.value }))}
                  helperText={reconexaoDe ? "Preenchido pelo HUB escolhido." : "O HUB nasce vinculado a este cliente."}
                  FormHelperTextProps={{ sx: { color: "#6B7280" } }}
                  InputLabelProps={{ sx: { color: "#6B7280" } }}
                  InputProps={{ sx: { color: "#fff", bgcolor: "#1F2937", borderRadius: 2 } }}
                >
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      #{t.id} {t.name}{t.active === false ? " (inativo)" : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                label="Nome do HUB (opcional)"
                fullWidth size="small" sx={{ mb: 2 }}
                value={registroForm.nomeHub}
                onChange={(e) => setRegistroForm((p) => ({ ...p, nomeHub: e.target.value }))}
                placeholder="Ex.: HUB Cliente XYZ"
                InputLabelProps={{ sx: { color: "#6B7280" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#1F2937", borderRadius: 2 } }}
              />
              <TextField
                label="Validade (minutos)"
                type="number" fullWidth size="small"
                value={registroForm.validadeMinutos}
                onChange={(e) => setRegistroForm((p) => ({ ...p, validadeMinutos: e.target.value }))}
                InputLabelProps={{ sx: { color: "#6B7280" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#1F2937", borderRadius: 2 } }}
              />
              {tokensPendentes.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#F59E0B", mb: 0.5 }}>
                    Tokens pendentes (emitidos e ainda nao usados)
                  </Typography>
                  {tokensPendentes.map((t, i) => (
                    <Typography key={i} sx={{ fontSize: 11, color: "#9CA3AF" }}>
                      {(t.nomeHub || "sem nome")} — expira {t.expiraEm ? new Date(t.expiraEm).toLocaleString() : "?"}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, color: "#F59E0B", fontWeight: 600, mb: 1.5 }}>
                Este token aparece UMA unica vez e vale para um unico registro. Copie agora.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, bgcolor: "#1F2937", borderRadius: 2, mb: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: "#25D366", fontFamily: "monospace", wordBreak: "break-all", flex: 1 }}>
                  {registroEmitido.token}
                </Typography>
                <Tooltip title="Copiar token">
                  <IconButton size="small" onClick={copiarToken} sx={{ color: "#6B48FF" }}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.6 }}>
                Expira em: {registroEmitido.expiraEm ? new Date(registroEmitido.expiraEm).toLocaleString() : "—"}
                <br />
                No HUB Local: Configuracoes → Conexao com HUB Central → informe a URL deste Central e este token → Registrar.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistroOpen(false)} disabled={registroEmitindo} sx={{ color: "#6B7280" }}>
            {registroEmitido ? "Fechar" : "Cancelar"}
          </Button>
          {!registroEmitido && (
            <Button
              onClick={emitirToken}
              disabled={registroEmitindo}
              variant="contained"
              sx={{ background: "linear-gradient(135deg, #6B48FF, #8B5CF6)", textTransform: "none", fontWeight: 700 }}
            >
              {registroEmitindo ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Emitir token"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
